// src/components/accueil/CarteAccueil.tsx — SCE Issues
// Carte de l'accueil V2 (Lot 9) : titre, contenu, pied.
//
// `defile` : la carte prend toute la hauteur de sa colonne et son contenu
// défile seul, en-tête et pied restant visibles (bureau). Sans, elle prend la
// hauteur de son contenu (téléphone : pas de défilement dans le défilement,
// pénible au pouce).
//
// `ajuste` : hauteur du contenu, mais la carte rétrécit (et son contenu
// défile) si son conteneur flex la plafonne.

import type { ReactNode } from 'react'

export function CarteAccueil({
  titre,
  compteur,
  sousTitre,
  action,
  pied,
  defile,
  ajuste,
  children,
}: {
  titre: ReactNode
  compteur?: number
  sousTitre?: string
  /** À droite du titre (bascule, lien). */
  action?: ReactNode
  pied?: ReactNode
  defile?: boolean
  ajuste?: boolean
  children: ReactNode
}): JSX.Element {
  return (
    <section
      style={{
        backgroundColor: 'var(--color-bg-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 12,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        ...(defile ? { minHeight: 0, height: '100%' } : {}),
        ...(ajuste ? { minHeight: 0, flexShrink: 1 } : {}),
      }}
    >
      <div
        className="flex items-center justify-between gap-3"
        style={{ padding: '14px 18px', borderBottom: '1px solid var(--color-gray-10)', flexShrink: 0 }}
      >
        <div className="min-w-0">
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 500, lineHeight: '22px' }}>
            {titre}
            {compteur !== undefined && (
              <span style={{ color: 'var(--color-text-secondary)', fontWeight: 400 }}> · {compteur}</span>
            )}
          </h2>
          {sousTitre && (
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>{sousTitre}</div>
          )}
        </div>
        {action}
      </div>

      <div style={defile || ajuste ? { flexGrow: defile ? 1 : 0, minHeight: 0, overflowY: 'auto' } : undefined}>{children}</div>

      {pied && <div style={{ borderTop: '1px solid var(--color-gray-10)', flexShrink: 0 }}>{pied}</div>}
    </section>
  )
}

/** Lien ou bouton de pied de carte : « Voir les 10 à qualifier › ». */
export function PiedCarte({ libelle, onClick }: { libelle: string; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center justify-between w-full"
      style={{
        minHeight: 48,
        padding: '0 18px',
        border: 'none',
        background: 'transparent',
        fontFamily: 'inherit',
        fontSize: 14,
        color: 'var(--color-text-primary)',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      {libelle}
      <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>›</span>
    </button>
  )
}

/** Message d'une carte vide ou en erreur. */
export function VideCarte({ children, erreur }: { children: ReactNode; erreur?: boolean }): JSX.Element {
  return (
    <div
      style={{
        padding: '18px',
        fontSize: 14,
        color: erreur ? 'var(--color-error)' : 'var(--color-text-secondary)',
      }}
    >
      {children}
    </div>
  )
}

/** Squelette de lignes pendant le chargement (gris 10 : posé dans une carte blanche). */
export function SqueletteLignes({ n = 4 }: { n?: number }): JSX.Element {
  return (
    <div style={{ padding: '10px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="animate-pulse" style={{ height: 40, borderRadius: 6, backgroundColor: 'var(--color-gray-10)' }} />
      ))}
    </div>
  )
}
